<script lang="ts">
  import { confirmAction } from '$lib/state/actionDialog.svelte';
  import { formatIST } from '$lib/utils/dates';
  // Self-service "Email" connect UI: a list of connected mailboxes (a user
  // may connect more than one — Gmail, company IMAP, etc.), each with
  // edit/test/sync/disconnect, plus an "Add inbox" button that opens the
  // same form for a new one. Shared between SettingsPage (the account's
  // permanent home), EmailsPage (rendered as the onboarding door when
  // nothing is connected yet), and Admin > Emails > All inboxes (managing
  // ANOTHER user's mailboxes) — extracted so all three stay in lockstep with
  // zero duplication. `api` defaults to the caller's own mailboxes
  // (meEmailAccountApi); the admin surface passes
  // adminEmailAccountsApi.forUser(userId) instead, which matches the same
  // list/create/save/remove/test/syncNow shape.
  // Passwords never come back from the API (see internal/api/handlers/
  // email_account.go's emailAccountJSON) so the form always starts with
  // blank password fields; leaving a password field blank on Save keeps the
  // previously-saved one untouched (see meEmailAccountApi.save / the
  // backend's updateMeEmailAccount).
  import { createMutation, createQuery, useQueryClient } from '@tanstack/svelte-query';
  import { ChevronDown, ChevronRight, Mail, Plus, Trash2, CheckCircle2, XCircle } from 'lucide-svelte';
  import { meEmailAccountApi } from '$lib/api/me';
  import { getErrorMessage } from '$lib/api/client';
  import { toast } from '$lib/state/toast.svelte';
  import type { EmailAccount, EmailAccountTestResult } from '$lib/types';

  type EmailAccountAPI = {
    list: () => Promise<{ results: EmailAccount[] }>;
    create: (body: Record<string, unknown>) => Promise<EmailAccount>;
    save: (id: number, body: Record<string, unknown>) => Promise<EmailAccount>;
    remove: (id: number) => Promise<{ detail: string }>;
    test: (id: number, body?: Record<string, unknown>) => Promise<EmailAccountTestResult>;
    syncNow: (id: number) => Promise<{ ran: boolean; account?: EmailAccount; detail?: string }>;
  };

  let {
    api = meEmailAccountApi,
    queryKey = ['me-email-accounts']
  }: { api?: EmailAccountAPI; queryKey?: unknown[] } = $props();

  const queryClient = useQueryClient();

  const accountsQuery = createQuery(() => ({
    queryKey,
    queryFn: () => api.list()
  }));
  const accounts = $derived(accountsQuery.data?.results ?? []);

  function invalidate() {
    queryClient.invalidateQueries({ queryKey });
  }

  function emptyForm() {
    return {
      email_address: '',
      imap_host: '',
      imap_port: 993,
      imap_username: '',
      imap_password: '',
      smtp_host: '',
      smtp_port: 465,
      smtp_username: '',
      smtp_password: ''
    };
  }
  // null = no form open, 'new' = adding an inbox, a number = editing that
  // account's id.
  let editing = $state<number | 'new' | null>(null);
  let form = $state(emptyForm());
  let formError = $state('');
  let testResult = $state<EmailAccountTestResult | null>(null);

  function openNew() {
    editing = 'new';
    form = emptyForm();
    formError = '';
    testResult = null;
  }

  function openEdit(a: EmailAccount) {
    editing = a.id;
    form = {
      email_address: a.email_address,
      imap_host: a.imap_host,
      imap_port: a.imap_port,
      imap_username: a.imap_username,
      imap_password: '',
      smtp_host: a.smtp_host,
      smtp_port: a.smtp_port,
      smtp_username: a.smtp_username,
      smtp_password: ''
    };
    formError = '';
    testResult = null;
  }

  function closeForm() {
    editing = null;
    formError = '';
    testResult = null;
  }

  function buildBody(): Record<string, unknown> {
    const body: Record<string, unknown> = {
      email_address: form.email_address.trim(),
      imap_host: form.imap_host.trim(),
      imap_port: Number(form.imap_port) || 993,
      imap_username: form.imap_username.trim(),
      smtp_host: form.smtp_host.trim(),
      smtp_port: Number(form.smtp_port) || 465,
      smtp_username: form.smtp_username.trim()
    };
    if (form.imap_password) body.imap_password = form.imap_password;
    if (form.smtp_password) body.smtp_password = form.smtp_password;
    return body;
  }

  const createAccountMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) => api.create(body),
    onSuccess: () => {
      closeForm();
      invalidate();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const saveMutation = createMutation(() => ({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) => api.save(id, body),
    onSuccess: () => {
      form.imap_password = '';
      form.smtp_password = '';
      invalidate();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  // Only meaningful once an account exists (the test endpoint is scoped by
  // :id) — the "Add inbox" form has no test action until it's saved.
  const testMutation = createMutation(() => ({
    mutationFn: ({ id, body }: { id: number; body: Record<string, unknown> }) => api.test(id, body),
    onSuccess: (result) => {
      testResult = result;
      // A test also updates the stored account's status/last_error server
      // side — refresh so the list reflects it.
      invalidate();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const removeMutation = createMutation(() => ({
    mutationFn: (id: number) => api.remove(id),
    onSuccess: (_result, id) => {
      if (editing === id) closeForm();
      invalidate();
    }
  }));

  const syncMutation = createMutation(() => ({
    mutationFn: (id: number) => api.syncNow(id),
    onSuccess: (result) => {
      toast.info(result.ran ? 'Sync complete.' : (result.detail ?? 'A sync is already in progress.'));
      invalidate();
    },
    onError: (err: unknown) => toast.error(getErrorMessage(err))
  }));

  function submit(e: SubmitEvent) {
    e.preventDefault();
    formError = '';
    testResult = null;
    if (editing === 'new') {
      createAccountMutation.mutate(buildBody());
    } else if (typeof editing === 'number') {
      saveMutation.mutate({ id: editing, body: buildBody() });
    }
  }

  function testConnection() {
    if (typeof editing !== 'number') return;
    formError = '';
    testMutation.mutate({ id: editing, body: buildBody() });
  }

  async function disconnect(a: EmailAccount) {
    if (await confirmAction(`Disconnect ${a.email_address || 'this mailbox'}?`)) removeMutation.mutate(a.id);
  }

  let gmailGuideOpen = $state(false);
  let roundmailGuideOpen = $state(false);
</script>

{#snippet accountForm()}
  <form class="tym-card space-y-4 p-4" onsubmit={submit}>
    <div>
      <label class="label" for="email_address">Email address</label>
      <input
        id="email_address"
        class="input"
        type="email"
        bind:value={form.email_address}
        placeholder="you@yourcompany.com"
        required
      />
    </div>

    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div class="space-y-3">
        <div class="text-[11px] font-semibold uppercase tracking-[.06em] text-dark-gray">IMAP (incoming)</div>
        <div>
          <label class="label" for="imap_host">Host</label>
          <input id="imap_host" class="input" bind:value={form.imap_host} placeholder="imap.example.com" />
        </div>
        <div>
          <label class="label" for="imap_port">Port</label>
          <input id="imap_port" class="input" type="number" bind:value={form.imap_port} />
        </div>
        <div>
          <label class="label" for="imap_username">Username</label>
          <input id="imap_username" class="input" bind:value={form.imap_username} />
        </div>
        <div>
          <label class="label" for="imap_password">Password</label>
          <input
            id="imap_password"
            class="input"
            type="password"
            bind:value={form.imap_password}
            placeholder={typeof editing === 'number' ? 'Leave blank to keep current' : ''}
            autocomplete="new-password"
          />
        </div>
      </div>

      <div class="space-y-3">
        <div class="text-[11px] font-semibold uppercase tracking-[.06em] text-dark-gray">SMTP (outgoing)</div>
        <div>
          <label class="label" for="smtp_host">Host</label>
          <input id="smtp_host" class="input" bind:value={form.smtp_host} placeholder="smtp.example.com" />
        </div>
        <div>
          <label class="label" for="smtp_port">Port</label>
          <input id="smtp_port" class="input" type="number" bind:value={form.smtp_port} />
        </div>
        <div>
          <label class="label" for="smtp_username">Username</label>
          <input id="smtp_username" class="input" bind:value={form.smtp_username} />
        </div>
        <div>
          <label class="label" for="smtp_password">Password</label>
          <input
            id="smtp_password"
            class="input"
            type="password"
            bind:value={form.smtp_password}
            placeholder={typeof editing === 'number' ? 'Leave blank to keep current' : ''}
            autocomplete="new-password"
          />
        </div>
      </div>
    </div>

    {#if testResult}
      <div class="space-y-1.5 rounded-lg border border-toyama-cloudy-gray bg-toyama-light-carbon p-3 text-[13px]">
        <div class="flex items-center gap-2">
          {#if testResult.imap.ok}
            <CheckCircle2 class="h-4 w-4 shrink-0 text-toyama-lime" />
            <span class="font-medium text-navy">IMAP: connected</span>
          {:else}
            <XCircle class="h-4 w-4 shrink-0 text-red-600" />
            <span class="font-medium text-navy">IMAP: {testResult.imap.error}</span>
          {/if}
        </div>
        <div class="flex items-center gap-2">
          {#if testResult.smtp.ok}
            <CheckCircle2 class="h-4 w-4 shrink-0 text-toyama-lime" />
            <span class="font-medium text-navy">SMTP: connected</span>
          {:else}
            <XCircle class="h-4 w-4 shrink-0 text-red-600" />
            <span class="font-medium text-navy">SMTP: {testResult.smtp.error}</span>
          {/if}
        </div>
      </div>
    {/if}

    {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}

    <div class="flex flex-wrap items-center justify-end gap-2 pt-2">
      <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
      {#if typeof editing === 'number'}
        <button
          type="button"
          class="btn-secondary"
          onclick={testConnection}
          disabled={testMutation.isPending}
        >
          {testMutation.isPending ? 'Testing...' : 'Test connection'}
        </button>
      {/if}
      <button
        type="submit"
        class="btn-primary"
        disabled={createAccountMutation.isPending || saveMutation.isPending}
      >
        {createAccountMutation.isPending || saveMutation.isPending ? 'Saving...' : 'Save'}
      </button>
    </div>
  </form>
{/snippet}

<div class="tym-page max-w-[720px]">
  <div class="mb-[18px] flex items-center gap-2.5">
    <Mail class="h-5 w-5 text-toyama-teal" />
    <div>
      <h2 class="tym-section-title">Email</h2>
      <div class="tym-section-sub">Connect one or more mailboxes so touchpoints can sync with your inbox.</div>
    </div>
  </div>

  {#if accountsQuery.isLoading}
    <p class="tym-loading-state">Loading&hellip;</p>
  {:else}
    <div class="space-y-3">
      {#each accounts as acct (acct.id)}
        <div class="tym-card flex flex-col gap-1.5 px-4 py-3">
          <div class="flex flex-wrap items-center justify-between gap-2">
            <div class="flex items-center gap-2.5">
              {#if acct.status === 'connected'}
                <CheckCircle2 class="h-4 w-4 shrink-0 text-toyama-lime" />
                <span class="text-[13px] font-medium text-navy">{acct.email_address}</span>
              {:else if acct.status === 'error'}
                <XCircle class="h-4 w-4 shrink-0 text-red-600" />
                <span class="text-[13px] font-medium text-navy">
                  {acct.email_address} &mdash; last check failed: {acct.last_error}
                </span>
              {:else}
                <span class="text-[13px] font-medium text-navy">{acct.email_address} &mdash; not tested yet</span>
              {/if}
            </div>
            <div class="flex shrink-0 flex-wrap items-center gap-1.5">
              <button type="button" class="btn-secondary" onclick={() => openEdit(acct)}>Edit</button>
              <button
                type="button"
                class="btn-secondary"
                onclick={() => syncMutation.mutate(acct.id)}
                disabled={syncMutation.isPending}
              >
                {syncMutation.isPending ? 'Syncing...' : 'Sync now'}
              </button>
              <button
                type="button"
                class="btn-danger"
                onclick={() => disconnect(acct)}
                disabled={removeMutation.isPending}
              >
                <Trash2 class="h-4 w-4" /> Disconnect
              </button>
            </div>
          </div>
          <div class="pl-[26px] text-[11.5px] text-dark-gray">
            {acct.last_synced_at ? `Last synced: ${formatIST(acct.last_synced_at)}` : 'Never synced yet.'}
          </div>
        </div>
        {#if editing === acct.id}
          {@render accountForm()}
        {/if}
      {/each}
    </div>

    {#if editing === 'new'}
      <div class="mt-3">
        {@render accountForm()}
      </div>
    {:else}
      <button type="button" class="btn-secondary mt-3" onclick={openNew}>
        <Plus class="h-4 w-4" /> Add inbox
      </button>
    {/if}
  {/if}

  <div class="mt-6 space-y-3">
    <button
      type="button"
      class="tym-card flex w-full items-center gap-2 px-4 py-3 text-left"
      onclick={() => (gmailGuideOpen = !gmailGuideOpen)}
    >
      {#if gmailGuideOpen}<ChevronDown class="h-4 w-4 shrink-0 text-dark-gray" />{:else}<ChevronRight
          class="h-4 w-4 shrink-0 text-dark-gray"
        />{/if}
      <span class="text-[13.5px] font-semibold text-navy">Gmail setup guide</span>
    </button>
    {#if gmailGuideOpen}
      <div class="tym-card space-y-2 px-4 py-3.5 text-[13px] text-dark-gray">
        <p>Gmail requires an <strong>app password</strong> — your normal Gmail password will not work.</p>
        <ol class="list-decimal space-y-1.5 pl-5">
          <li>Make sure 2-Step Verification is turned on for your Google account.</li>
          <li>
            Go to
            <a
              class="text-toyama-teal underline"
              href="https://myaccount.google.com/apppasswords"
              target="_blank"
              rel="noopener noreferrer">myaccount.google.com/apppasswords</a
            >
            (Google Account &rarr; Security &rarr; 2-Step Verification &rarr; App passwords).
          </li>
          <li>Create an app password named <strong>"Toyama CRM"</strong>.</li>
          <li>Copy the 16-character password Google shows you.</li>
          <li>
            Use these settings: IMAP host <code>imap.gmail.com</code> port <code>993</code> (SSL), SMTP host
            <code>smtp.gmail.com</code> port <code>465</code> (SSL). Username is your full Gmail address; password
            is the app password you just copied &mdash; <strong>not</strong> your normal Gmail password.
          </li>
          <li>
            On an older Gmail account, you may also need to enable IMAP under Gmail &rarr; Settings &rarr;
            Forwarding and POP/IMAP.
          </li>
        </ol>
      </div>
    {/if}

    <button
      type="button"
      class="tym-card flex w-full items-center gap-2 px-4 py-3 text-left"
      onclick={() => (roundmailGuideOpen = !roundmailGuideOpen)}
    >
      {#if roundmailGuideOpen}<ChevronDown class="h-4 w-4 shrink-0 text-dark-gray" />{:else}<ChevronRight
          class="h-4 w-4 shrink-0 text-dark-gray"
        />{/if}
      <span class="text-[13.5px] font-semibold text-navy">Roundmail (company mail) setup guide</span>
    </button>
    {#if roundmailGuideOpen}
      <div class="tym-card space-y-2 px-4 py-3.5 text-[13px] text-dark-gray">
        <p>For a company mailbox, use these settings (ask an admin for your exact mail host if unsure):</p>
        <ul class="list-disc space-y-1.5 pl-5">
          <li>IMAP host <code>mail.&lt;yourdomain&gt;</code>, port <code>993</code>, SSL</li>
          <li>SMTP host <code>mail.&lt;yourdomain&gt;</code>, port <code>465</code>, SSL</li>
          <li>Username: your full email address</li>
          <li>Password: your mailbox password</li>
        </ul>
      </div>
    {/if}
  </div>
</div>
